import { replaceVariables, SAMPLE_DATA, SAMPLE_TRANSACTIONS } from '@/components/settings/sample-data';
import { DEFAULT_AGEING_BANDS } from '@/hooks/useAgeingBands';
import {
  type AgeingBasis,
  buildDebitOnlyStatementFigures,
  filterDebitTransactions,
} from '@/lib/react-pdf/statement-credit-exclusion';
import { formatCurrency, formatDateDDMMYYYY } from '@/lib/utils';
import type { AgeingBandConfig } from '@/types/ageing-analysis';
import type { TemplateBuilderConfig } from '@/types/template-builder';
import type { Transaction } from '@/types/transaction';
import type { CustomerAgeingUI } from '@/types/ui/customer-detail';

/** Map legacy hardcoded bucket keys to default band sequence numbers */
const LEGACY_BUCKET_TO_SEQUENCE: Record<string, number> = {
  current: 1,
  days1_30: 2,
  days31_60: 3,
  days61_90: 4,
  days91_120: 5,
  days120Plus: 5,
};

export function migrateLegacyBuckets(
  buckets: Record<string, boolean>,
  ageingBands: AgeingBandConfig[],
): Record<string, boolean> {
  const keys = Object.keys(buckets);
  const isLegacy = keys.some((k) => k in LEGACY_BUCKET_TO_SEQUENCE);
  if (!isLegacy) return buckets;

  const migrated: Record<string, boolean> = {};
  for (const band of ageingBands) {
    migrated[String(band.sequence)] = false;
  }
  for (const [key, enabled] of Object.entries(buckets)) {
    const seq = LEGACY_BUCKET_TO_SEQUENCE[key];
    if (seq !== undefined && enabled) {
      migrated[String(seq)] = true;
    }
  }
  return migrated;
}

/**
 * Reconcile saved bucket config against the current system ageing bands.
 * - Migrates legacy keys to sequence-based keys
 * - Removes entries for bands that no longer exist (handles band removal)
 * - Does NOT add entries for new bands (new bands require explicit opt-in in the template builder)
 */
export function reconcileBuckets(
  savedBuckets: Record<string, boolean>,
  currentBands: AgeingBandConfig[],
): Record<string, boolean> {
  const migrated = migrateLegacyBuckets(savedBuckets, currentBands);
  const currentSequences = new Set(currentBands.map((b) => String(b.sequence)));
  const reconciled: Record<string, boolean> = {};
  for (const [key, enabled] of Object.entries(migrated)) {
    if (currentSequences.has(key)) {
      reconciled[key] = enabled;
    }
  }
  return reconciled;
}

export interface StatementDocumentData {
  // Header dynamic items
  companyAddress: string;
  companyCountry: string;
  documentReference: string;
  documentDate: string;
  // Customer address
  customerName: string;
  customerAddressLine: string;
  customerCity: string;
  customerCounty: string;
  customerPostcode: string;
  customerCountry: string;
  customerAddressContent: string;
  // Reference block
  accountNumber: string;
  currencyCode: string;
  paymentTerms: string;
  statementDate: string;
  // Account summary
  totalOutstanding: string;
  totalOverdue: string;
  totalNotYetDue: string;
  creditLimit: string;
  availableCredit: string;
  lastPaymentAmount: string;
  lastPaymentDate: string;
  // Transaction table: rows × enabled-columns
  transactions: string[][];
  // Whether each transaction row is overdue (parallel to transactions array)
  transactionOverdue: boolean[];
  // Totals
  totalInvoiced: string;
  // Ageing: one value per enabled bucket (in bucket order)
  agingBuckets: string[];
  // Labels for each enabled ageing bucket (parallel to agingBuckets)
  agingBucketLabels: string[];
  // Whether each ageing bucket is overdue (startDays > 0), parallel to agingBuckets
  agingBucketOverdue: boolean[];
  // Resolved text content (variables substituted in sample mode, raw in placeholder mode)
  documentTitle: string;
  introText: string;
  paymentInstructions: string;
  closingText: string;
}

const COLUMN_FIELD_MAP: Record<string, keyof (typeof SAMPLE_TRANSACTIONS)[number]> = {
  invoice_no: 'reference_number',
  invoice_date: 'transaction_date',
  due_date: 'due_date',
  description: 'transaction_description',
  invoice_amount: 'gross_amount',
  balance_outstanding: 'outstanding_amount',
  days_overdue: 'days_overdue',
  po_number: 'order_number',
  transaction_type: 'transaction_type',
  disputed: 'isDisputed',
};

function getEnabledBuckets(config: TemplateBuilderConfig): string[] {
  return Object.entries(config.fieldsSettings.agingSummary.buckets)
    .filter(([, enabled]) => enabled)
    .map(([key]) => key);
}

function getBandForSequence(bands: AgeingBandConfig[], seq: number): AgeingBandConfig | undefined {
  return bands.find((b) => b.sequence === seq);
}

function generateSampleAgeingValues(bands: AgeingBandConfig[]): Record<number, string> {
  const sampleAmounts = [5500, 2250, 3500, 1750, 1250, 2000];
  const result: Record<number, string> = {};
  for (let i = 0; i < bands.length; i++) {
    const amount = sampleAmounts[i % sampleAmounts.length] ?? 0;
    result[bands[i].sequence] = formatCurrency(amount, 'GBP');
  }
  return result;
}

// Parse dd/MM/yyyy date strings used in SAMPLE_TRANSACTIONS for sort comparisons
function parseDMY(dateStr: string): number {
  const parts = dateStr.split('/');
  if (parts.length !== 3) return 0;
  const [d, m, y] = parts.map(Number);
  return new Date(y, m - 1, d).getTime();
}

function sortTransactions(transactions: typeof SAMPLE_TRANSACTIONS, sortBy: string): typeof SAMPLE_TRANSACTIONS {
  const sorted = [...transactions];
  switch (sortBy) {
    case 'newest-first':
      sorted.sort((a, b) => parseDMY(b.transaction_date) - parseDMY(a.transaction_date));
      break;
    case 'due-date-asc':
      sorted.sort((a, b) => parseDMY(a.due_date) - parseDMY(b.due_date));
      break;
    case 'due-date-desc':
      sorted.sort((a, b) => parseDMY(b.due_date) - parseDMY(a.due_date));
      break;
    case 'invoice-number-asc':
      sorted.sort((a, b) => a.reference_number.localeCompare(b.reference_number));
      break;
    default:
      sorted.sort((a, b) => parseDMY(a.transaction_date) - parseDMY(b.transaction_date));
  }
  return sorted;
}

export function buildSampleData(
  config: TemplateBuilderConfig,
  ageingBands?: AgeingBandConfig[],
): StatementDocumentData {
  const bands = ageingBands ?? DEFAULT_AGEING_BANDS;
  const enabledColumns = config.fieldsSettings.transactionTable.columns.filter((c) => c.enabled);
  const enabledBuckets = getEnabledBuckets(config);
  const sortedTransactions = sortTransactions(SAMPLE_TRANSACTIONS, config.fieldsSettings.transactionTable.sortBy);
  const sampleAgeing = generateSampleAgeingValues(bands);
  return {
    companyAddress: SAMPLE_DATA.companyAddress,
    companyCountry: SAMPLE_DATA.companyCountry,
    documentReference: SAMPLE_DATA.documentReference,
    documentDate: SAMPLE_DATA.statementDate,
    customerName: SAMPLE_DATA.customerName,
    customerAddressLine: SAMPLE_DATA.customerAddressLine,
    customerCity: SAMPLE_DATA.customerCity,
    customerCounty: SAMPLE_DATA.customerCounty,
    customerPostcode: SAMPLE_DATA.customerPostcode,
    customerCountry: SAMPLE_DATA.customerCountry,
    accountNumber: SAMPLE_DATA.accountNumber,
    currencyCode: SAMPLE_DATA.currencyCode,
    paymentTerms: SAMPLE_DATA.paymentTerms,
    statementDate: SAMPLE_DATA.statementDate,
    totalOutstanding: SAMPLE_DATA.totalOutstanding,
    totalOverdue: SAMPLE_DATA.totalOverdue,
    totalNotYetDue: '£5,500.00',
    creditLimit: SAMPLE_DATA.creditLimit,
    availableCredit: SAMPLE_DATA.availableCredit,
    lastPaymentAmount: SAMPLE_DATA.lastPaymentAmount,
    lastPaymentDate: SAMPLE_DATA.lastPaymentDate,
    transactions: sortedTransactions.map((row) =>
      enabledColumns.map((col) => {
        const field = COLUMN_FIELD_MAP[col.id];
        if (!field) return '-';
        const v = row[field];
        if (typeof v === 'boolean') return v ? 'Yes' : '—';
        return typeof v === 'string' || typeof v === 'number' ? String(v) : '-';
      }),
    ),
    transactionOverdue: sortedTransactions.map((row) => row.isOverdue),
    totalInvoiced: SAMPLE_DATA.totalInvoiced,
    agingBuckets: enabledBuckets.map((key) => {
      const seq = parseInt(key, 10);
      return sampleAgeing[seq] ?? '-';
    }),
    agingBucketLabels: enabledBuckets.map((key) => {
      const seq = parseInt(key, 10);
      const band = getBandForSequence(bands, seq);
      return band?.bandLabel ?? `Band ${key}`;
    }),
    agingBucketOverdue: enabledBuckets.map((key) => {
      const seq = parseInt(key, 10);
      const band = getBandForSequence(bands, seq);
      return band ? band.startDays > 0 : true;
    }),
    documentTitle: replaceVariables(config.contentSettings.documentTitle, true),
    introText: replaceVariables(config.contentSettings.introText.content, true),
    paymentInstructions: replaceVariables(config.contentSettings.paymentInstructions.content, true),
    closingText: replaceVariables(config.contentSettings.closingText.content, true),
    customerAddressContent: stripBlankLines(replaceVariables(config.contentSettings.customerAddress.content, true)),
  };
}

export interface CustomerPreviewInput {
  name: string;
  accountNumber: string;
  currency: string;
  outstanding: number;
  overdue: number;
  creditLimit: number;
  currencyCreditLimit?: number;
  address: string;
  addressLine?: string;
  town?: string;
  county?: string;
  postcode?: string;
  country?: string;
  paymentTerms: string;
  lastPayment?: string;
  lastPaymentAmount?: number;
  lastPaymentCurrencyAmount?: number | null;
  isAnyCurrency?: boolean;
  baseCurrency?: string;
  displayOutstanding?: number;
  displayOverdue?: number;
  outstandingHome?: number;
  overdueHome?: number;
  customerEmail?: string;
  customerPhone?: string;
}

function formatDateForStatement(date: Date): string {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${d}/${m}/${date.getFullYear()}`;
}

export function resolveStatementCurrency(
  customer: Pick<CustomerPreviewInput, 'currency' | 'baseCurrency' | 'isAnyCurrency'>,
  currencyMode?: 'customer' | 'base',
): string {
  const baseCur = customer.baseCurrency ?? customer.currency;
  const isNonBaseCurrency = !customer.isAnyCurrency && customer.currency !== baseCur;
  return currencyMode === 'base' ? baseCur : isNonBaseCurrency ? customer.currency : baseCur;
}

export function resolveEffectiveCurrency(
  customer: CustomerPreviewInput,
  currencyMode?: 'customer' | 'base',
): { currency: string; outstanding: number; overdue: number } {
  const effectiveCurrency = resolveStatementCurrency(customer, currencyMode);
  const baseCur = customer.baseCurrency ?? customer.currency;
  const useBase = effectiveCurrency === baseCur;
  const outstanding = useBase
    ? (customer.outstandingHome ?? customer.outstanding)
    : (customer.displayOutstanding ?? customer.outstanding);
  const overdue = useBase ? (customer.overdueHome ?? customer.overdue) : (customer.displayOverdue ?? customer.overdue);
  return { currency: effectiveCurrency, outstanding, overdue };
}

function buildCustomerVariableMap(
  customer: CustomerPreviewInput,
  statementDate: Date,
  currencyMode?: 'customer' | 'base',
  balanceOverride?: { outstanding: number; overdue: number },
): Record<string, string> {
  const {
    currency: cur,
    outstanding: netOutstanding,
    overdue: netOverdue,
  } = resolveEffectiveCurrency(customer, currencyMode);
  const outstanding = balanceOverride?.outstanding ?? netOutstanding;
  const overdue = balanceOverride?.overdue ?? netOverdue;
  const baseCur = customer.baseCurrency ?? customer.currency;
  const isNonBaseCurrency = !customer.isAnyCurrency && customer.currency !== baseCur;
  const effectiveCreditLimit =
    currencyMode === 'base'
      ? customer.creditLimit
      : isNonBaseCurrency
        ? (customer.currencyCreditLimit ?? customer.creditLimit)
        : customer.creditLimit;
  const avail = Math.max(0, effectiveCreditLimit - outstanding);
  const stmtDate = formatDateForStatement(statementDate);
  return {
    customerName: customer.name,
    customerEmail: customer.customerEmail || '',
    customerPhone: customer.customerPhone || '',
    customerAddressLine: customer.addressLine || '',
    customerAddressLine1: customer.addressLine || '',
    customerCity: customer.town || '',
    customerCounty: customer.county || '',
    customerPostcode: customer.postcode || '',
    customerCountry: customer.country || '',
    accountNumber: customer.accountNumber,
    currencyCode: cur,
    paymentTerms: customer.paymentTerms,
    statementDate: stmtDate,
    totalOutstanding: formatCurrency(outstanding, cur),
    totalOverdue: formatCurrency(overdue, cur),
    totalNotYetDue: formatCurrency(outstanding - overdue, cur),
    creditLimit: formatCurrency(effectiveCreditLimit, cur),
    availableCredit: formatCurrency(avail, cur),
    accountBalance: formatCurrency(outstanding, cur),
    overdueAmount: formatCurrency(overdue, cur),
  };
}

function buildCompanyVariableMap(config: TemplateBuilderConfig): Record<string, string> {
  return {
    companyName: config.companyName || '',
    companyEmail: config.companyEmail || '',
    companyPhone: config.companyPhone || '',
    companyAddress: config.companyAddress || '',
    companyCountry: config.companyCountry || '',
    companyRegNumber: config.companyReg || '',
    companyVatNumber: config.companyVat || '',
    companyBankName: config.companyBankName || '',
    sortCode: config.sortCode || '',
    bankAccountNumber: config.bankAccountNumber || '',
    ibanNumber: config.ibanNumber || '',
    swiftBicCode: config.swiftCode || '',
  };
}

function stripBlankLines(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((line) => line.trim() !== '')
    .join('\n');
}

function replaceVariablesWithCustomerData(
  text: string,
  customerVars: Record<string, string>,
  companyVars: Record<string, string>,
): string {
  if (!text) return '';
  let result = text;
  for (const [key, value] of Object.entries(customerVars)) {
    result = result.replace(new RegExp(`<<${key}>>`, 'g'), value);
  }
  for (const [key, value] of Object.entries(companyVars)) {
    result = result.replace(new RegExp(`<<${key}>>`, 'g'), value);
  }
  // Remove any remaining unresolved placeholders (no sample data fallback)
  result = result.replace(/<<[a-zA-Z0-9_]+>>/g, '');
  return result;
}

function sortRealTransactions(transactions: Transaction[], sortBy: string): Transaction[] {
  const sorted = [...transactions];
  switch (sortBy) {
    case 'newest-first':
      sorted.sort((a, b) => b.transactionDate.localeCompare(a.transactionDate));
      break;
    case 'due-date-asc':
      sorted.sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''));
      break;
    case 'due-date-desc':
      sorted.sort((a, b) => (b.dueDate ?? '').localeCompare(a.dueDate ?? ''));
      break;
    case 'invoice-number-asc':
      sorted.sort((a, b) => a.referenceNumber.localeCompare(b.referenceNumber));
      break;
    default:
      sorted.sort((a, b) => a.transactionDate.localeCompare(b.transactionDate));
  }
  return sorted;
}

export interface StatementBuildOptions {
  /** Omit credit notes, credit adjustments and unallocated receipts, recalculating totals and ageing from the debits */
  excludeCreditTransactions?: boolean;
  /** Company metrics setting used when recalculating ageing bands (defaults to due-date) */
  ageingBasis?: AgeingBasis;
}

export function buildCustomerPreviewData(
  config: TemplateBuilderConfig,
  customer: CustomerPreviewInput,
  transactions: Transaction[],
  ageing: CustomerAgeingUI | null,
  statementDate: Date,
  currencyMode?: 'customer' | 'base',
  ageingBands?: AgeingBandConfig[],
  options?: StatementBuildOptions,
): StatementDocumentData {
  const {
    currency,
    outstanding: netOutstanding,
    overdue: netOverdue,
  } = resolveEffectiveCurrency(customer, currencyMode);
  const baseCur = customer.baseCurrency ?? customer.currency;
  const useBase = currency === baseCur;
  const enabledColumns = config.fieldsSettings.transactionTable.columns.filter((c) => c.enabled);
  const enabledBuckets = getEnabledBuckets(config);
  const includedTransactions = options?.excludeCreditTransactions
    ? filterDebitTransactions(transactions)
    : transactions;
  const debitOnlyFigures = options?.excludeCreditTransactions
    ? buildDebitOnlyStatementFigures(
        includedTransactions,
        ageingBands ?? DEFAULT_AGEING_BANDS,
        useBase,
        options.ageingBasis ?? 'due-date',
      )
    : null;
  const outstanding = debitOnlyFigures?.outstanding ?? netOutstanding;
  const overdue = debitOnlyFigures?.overdue ?? netOverdue;
  const effectiveAgeing = debitOnlyFigures?.ageing ?? ageing;
  const sortedTransactions = sortRealTransactions(includedTransactions, config.fieldsSettings.transactionTable.sortBy);
  const stmtDate = formatDateForStatement(statementDate);
  const baseCurPreview = customer.baseCurrency ?? customer.currency;
  const isNonBaseCurrencyPreview = !customer.isAnyCurrency && customer.currency !== baseCurPreview;
  const effectiveCreditLimitPreview =
    currencyMode === 'base'
      ? customer.creditLimit
      : isNonBaseCurrencyPreview
        ? (customer.currencyCreditLimit ?? customer.creditLimit)
        : customer.creditLimit;
  const availableCredit = Math.max(0, effectiveCreditLimitPreview - outstanding);
  const customerVars = buildCustomerVariableMap(
    customer,
    statementDate,
    currencyMode,
    debitOnlyFigures ? { outstanding, overdue } : undefined,
  );
  const companyVars = buildCompanyVariableMap(config);

  return {
    companyAddress: config.companyAddress || '',
    companyCountry: config.companyCountry || '',
    documentReference: `STMT-${customer.accountNumber}`,
    documentDate: stmtDate,
    customerName: customer.name,
    customerAddressLine: customer.addressLine || '',
    customerCity: customer.town || '',
    customerCounty: customer.county || '',
    customerPostcode: customer.postcode || '',
    customerCountry: customer.country || '',
    accountNumber: customer.accountNumber,
    currencyCode: currency,
    paymentTerms: customer.paymentTerms,
    statementDate: stmtDate,
    totalOutstanding: formatCurrency(outstanding, currency),
    totalOverdue: formatCurrency(overdue, currency),
    totalNotYetDue: formatCurrency(outstanding - overdue, currency),
    creditLimit: formatCurrency(effectiveCreditLimitPreview, currency),
    availableCredit: formatCurrency(availableCredit, currency),
    lastPaymentAmount:
      customer.lastPaymentAmount != null
        ? formatCurrency(
            currencyMode === 'customer' &&
              currency !== (customer.baseCurrency ?? customer.currency) &&
              customer.lastPaymentCurrencyAmount != null
              ? customer.lastPaymentCurrencyAmount
              : customer.lastPaymentAmount,
            currency,
          )
        : '-',
    lastPaymentDate: customer.lastPayment ? formatDateDDMMYYYY(customer.lastPayment) : '-',
    transactions: sortedTransactions.map((tx) =>
      enabledColumns.map((col) => {
        switch (col.id) {
          case 'invoice_no':
            return tx.referenceNumber;
          case 'invoice_date':
            return formatDateDDMMYYYY(tx.transactionDate);
          case 'due_date':
            return tx.dueDate ? formatDateDDMMYYYY(tx.dueDate) : '-';
          case 'invoice_amount':
            return formatCurrency(useBase ? tx.amount : (tx.displayAmount ?? tx.amount), currency);
          case 'balance_outstanding':
            return formatCurrency(
              useBase
                ? (tx.outstandingAmount ?? tx.amount)
                : (tx.displayOutstandingAmount ?? tx.outstandingAmount ?? tx.amount),
              currency,
            );
          case 'description':
            return tx.description || '-';
          case 'po_number':
            return tx.orderNumber || '-';
          case 'days_overdue': {
            if (tx.status !== 'overdue') return '-';
            const days =
              tx.daysOverdue ??
              (tx.dueDate ? Math.floor((statementDate.getTime() - new Date(tx.dueDate).getTime()) / 86_400_000) : 0);
            return days > 0 ? String(days) : '-';
          }
          case 'transaction_type':
            return tx.transactionType === 'invoice'
              ? 'Invoice'
              : tx.transactionType === 'credit_note'
                ? 'Credit Note'
                : tx.transactionType === 'receipt'
                  ? 'Receipt'
                  : tx.transactionType === 'debit_adjustment'
                    ? 'Debit Adjustment'
                    : tx.transactionType === 'credit_adjustment'
                      ? 'Credit Adjustment'
                      : '-';
          case 'disputed':
            return (tx.onDispute ?? false) ? 'Yes' : '—';
          default:
            return '-';
        }
      }),
    ),
    transactionOverdue: sortedTransactions.map((tx) => tx.status === 'overdue'),
    totalInvoiced: formatCurrency(
      includedTransactions.reduce((sum, tx) => sum + (useBase ? tx.amount : (tx.displayAmount ?? tx.amount)), 0),
      currency,
    ),
    agingBuckets: enabledBuckets.map((key) => {
      if (!effectiveAgeing) return '-';
      const seq = parseInt(key, 10);
      const band = effectiveAgeing.bands.find((b) => b.sequence === seq);
      return band ? formatCurrency(band.totalOutstanding, currency) : formatCurrency(0, currency);
    }),
    agingBucketLabels: (() => {
      const bands = ageingBands ?? DEFAULT_AGEING_BANDS;
      return enabledBuckets.map((key) => {
        const seq = parseInt(key, 10);
        const band = getBandForSequence(bands, seq);
        return band?.bandLabel ?? `Band ${key}`;
      });
    })(),
    agingBucketOverdue: (() => {
      const bands = ageingBands ?? DEFAULT_AGEING_BANDS;
      return enabledBuckets.map((key) => {
        const seq = parseInt(key, 10);
        const band = getBandForSequence(bands, seq);
        return band ? band.startDays > 0 : true;
      });
    })(),
    documentTitle: replaceVariablesWithCustomerData(config.contentSettings.documentTitle, customerVars, companyVars),
    introText: replaceVariablesWithCustomerData(config.contentSettings.introText.content, customerVars, companyVars),
    paymentInstructions: replaceVariablesWithCustomerData(
      config.contentSettings.paymentInstructions.content,
      customerVars,
      companyVars,
    ),
    closingText: replaceVariablesWithCustomerData(
      config.contentSettings.closingText.content,
      customerVars,
      companyVars,
    ),
    customerAddressContent: stripBlankLines(
      replaceVariablesWithCustomerData(config.contentSettings.customerAddress.content, customerVars, companyVars),
    ),
  };
}

export function buildVariablePlaceholderData(
  config: TemplateBuilderConfig,
  ageingBands?: AgeingBandConfig[],
): StatementDocumentData {
  const bands = ageingBands ?? DEFAULT_AGEING_BANDS;
  const enabledColumns = config.fieldsSettings.transactionTable.columns.filter((c) => c.enabled);
  const enabledBuckets = getEnabledBuckets(config);
  return {
    companyAddress: '<<companyAddress>>',
    companyCountry: '<<companyCountry>>',
    documentReference: '<<documentReference>>',
    documentDate: '<<statementDate>>',
    customerName: '<<customerName>>',
    customerAddressLine: '<<customerAddressLine>>',
    customerCity: '<<customerCity>>',
    customerCounty: '<<customerCounty>>',
    customerPostcode: '<<customerPostcode>>',
    customerCountry: '<<customerCountry>>',
    accountNumber: '<<accountNumber>>',
    currencyCode: '<<currencyCode>>',
    paymentTerms: '<<paymentTerms>>',
    statementDate: '<<statementDate>>',
    totalOutstanding: '<<totalOutstanding>>',
    totalOverdue: '<<totalOverdue>>',
    totalNotYetDue: '<<totalNotYetDue>>',
    creditLimit: '<<creditLimit>>',
    availableCredit: '<<availableCredit>>',
    lastPaymentAmount: '<<lastPaymentAmount>>',
    lastPaymentDate: '<<lastPaymentDate>>',
    transactions: [enabledColumns.map((col) => col.variable)],
    transactionOverdue: [false],
    totalInvoiced: '<<totalInvoiced>>',
    agingBuckets: enabledBuckets.map((key) => `<<ageing_band_${key}>>`),
    agingBucketLabels: enabledBuckets.map((key) => {
      const seq = parseInt(key, 10);
      const band = getBandForSequence(bands, seq);
      return band?.bandLabel ?? `Band ${key}`;
    }),
    agingBucketOverdue: enabledBuckets.map((key) => {
      const seq = parseInt(key, 10);
      const band = getBandForSequence(bands, seq);
      return band ? band.startDays > 0 : true;
    }),
    documentTitle: config.contentSettings.documentTitle,
    introText: config.contentSettings.introText.content,
    paymentInstructions: config.contentSettings.paymentInstructions.content,
    closingText: config.contentSettings.closingText.content,
    customerAddressContent: config.contentSettings.customerAddress.content,
  };
}
